import axios, {
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { useUserStore } from '@/store/userStore';
import { getDefaultRequestHeaders } from '@/utils/appInfo';
import { tokenManager } from '../utils/token';

export class UnauthorizedError extends Error {
  constructor(message: string = '未登录，请先登录') {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

/**
 * 请求配置扩展
 */
type _AxiosRequestConfig = AxiosRequestConfig & {
  /** 是否跳过 token 验证，某些公开接口不需要 token */
  skipAuth?: boolean;
};

/**
 * API 客户端类
 */
class ApiClient {
  private instance: AxiosInstance;

  private async clearAuthState() {
    try {
      await useUserStore.getState().logout();
    } catch (error) {
      console.error('Clear auth state failed:', error);
    }
  }

  constructor(baseURL: string) {
    // 创建 axios 实例
    this.instance = axios.create({
      baseURL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 请求拦截器
    this.instance.interceptors.request.use(
      async (config) => {
        const headers = AxiosHeaders.from(config.headers);
        const defaultHeaders = getDefaultRequestHeaders();

        Object.entries(defaultHeaders).forEach(([key, value]) => {
          if (!headers.has(key)) {
            headers.set(key, value);
          }
        });

        // 如果需要 token，从 tokenManager 获取
        if (!(config as _AxiosRequestConfig).skipAuth) {
          const token = await tokenManager.getAccessToken();
          if (token) {
            headers.set('Authorization', `Bearer ${token}`);
          } else {
            await this.clearAuthState();
            throw new UnauthorizedError('未登录，请先登录');
          }
        }

        config.headers = headers;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        if (error.response?.status === 401) {
          await this.clearAuthState();
          return Promise.reject(new UnauthorizedError('登录已过期，请重新登录'));
        }

        return Promise.reject(error);
      },
    );
  }

  /**
   * 通用请求方法
   * @param config 请求配置
   * @returns 响应数据
   */
  private async request<T>(config: _AxiosRequestConfig): Promise<T> {
    try {
      // 发送请求
      const response = await this.instance.request<T, AxiosResponse<T>>(config);

      return response.data;
    } catch (error) {
      // 处理其他错误
      if (error instanceof UnauthorizedError) {
        throw error;
      }

      // 处理 axios 错误
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || error.message || '请求失败');
      }

      throw error;
    }
  }

  /**
   * GET 请求 - 必须传入响应类型泛型，不能为 any
   * @param url 请求地址
   * @param params 查询参数
   * @param config 额外配置
   * @returns 响应数据
   */
  async get<T>(
    url: string,
    params?: Record<string, unknown>,
    config?: _AxiosRequestConfig,
  ): Promise<T> {
    return this.request<T>({
      ...config,
      params,
      method: 'GET',
      url,
    });
  }

  /**
   * POST 请求 - 必须传入请求体和响应类型泛型，不能为 any
   * @param url 请求地址
   * @param data 请求数据
   * @param config 额外配置
   * @returns 响应数据
   */
  async post<T>(url: string, data?: unknown, config?: _AxiosRequestConfig): Promise<T> {
    return this.request<T>({
      ...config,
      data,
      method: 'POST',
      url,
    });
  }
}

/**
 * 获取环境变量
 */
const baseURL = import.meta.env.VITE_BASE_URL;

/**
 * 导出单例
 */
export const apiClient = new ApiClient(baseURL);

/**
 * API 响应结构类型
 */
export type ApiResponse<T> = {
  /** 状态码 */
  code: number;
  /** 提示消息 */
  message: string;
  /** 响应数据 */
  data: T;
};
